// 먼저, 키의 프리픽스를 정의합니다.
type Key = `local-${StorageKey}` | `session-${StorageKey}`;

import { StorageKey } from "#/unique/domain";
// useStorage 훅을 정의합니다.
import { useState, useEffect, useMemo, useCallback } from "react";

export const useStorage = <T>(key: Key) => {
  const storage = useMemo(() => {
    if (key.startsWith("local-")) {
      return window.localStorage;
    } else if (key.startsWith("session-")) {
      return window.sessionStorage;
    } else {
      throw new Error(
        `Invalid storage key prefix for "${key}". Use "local-" or "session-" as prefix.`
      );
    }
  }, [key]);

  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    if (storage) {
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : undefined;
    }
    return undefined;
  });

  const setValue = useCallback(
    (value?: T) => {
      try {
        setStoredValue(value);
        if (value) {
          storage.setItem(key, JSON.stringify(value));
        } else {
          storage.removeItem(key);
        }
      } catch (error) {
        console.error(`Error setting storage key "${key}":`, error);
      }
    },
    [key, storage]
  );

  useEffect(() => {
    const injectValue = () => {
      const item = storage.getItem(key);
      const parsedItem = item ? JSON.parse(item) : undefined;
      setStoredValue(parsedItem);
    };

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === key) injectValue();
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [key, storage]);

  return [storedValue, setValue] as [T | undefined, (value?: T) => void];
};
